import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { libraryApi } from './api';
import { ASSETS } from '../../config/assets';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Upload, Image, FileText, Trash2, LogOut, Shield, X,
  Search, FolderOpen, Pencil, Check, FolderPlus, ArrowLeft,
  Files, ChevronRight,
} from 'lucide-react';

const MarcoLibrary = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, fr) => language === 'fr' ? fr : en;

  const canManage = user?.role === 'super_admin' || user?.role === 'executive';

  const [folders, setFolders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null); // null = root view
  const [filter, setFilter] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteFolderId, setDeleteFolderId] = useState(null);

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  // Folder create/edit state
  const [showFolderCreate, setShowFolderCreate] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderDesc, setFolderDesc] = useState('');
  const [editFolderId, setEditFolderId] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderDesc, setEditFolderDesc] = useState('');

  // Edit asset
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, [currentFolder]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (currentFolder === null) {
        // Root view: load folders and unfiled assets
        const [foldersData, assetsData] = await Promise.all([
          libraryApi.listFolders(),
          libraryApi.list(''),  // empty string = unfiled
        ]);
        setFolders(foldersData);
        setAssets(assetsData);
      } else {
        // Inside a folder: load folder assets
        const assetsData = await libraryApi.list(currentFolder.id);
        setAssets(assetsData);
      }
    } catch (err) {
      console.error('Failed to load library:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── File Upload (single or bulk) ───
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['png', 'jpg', 'jpeg', 'pdf'].includes(ext) && f.size <= 10 * 1024 * 1024;
    });
    if (valid.length === 0) {
      setUploadError(t('No valid files selected (PNG/JPG/PDF, max 10MB).', 'Aucun fichier valide selectionne.'));
      return;
    }
    setUploadError('');
    setUploadFiles(valid);
    if (valid.length === 1 && !uploadTitle) {
      setUploadTitle(valid[0].name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    setUploadError('');
    try {
      const folderId = currentFolder?.id || '';
      if (uploadFiles.length === 1) {
        const formData = new FormData();
        formData.append('file', uploadFiles[0]);
        formData.append('title', uploadTitle.trim() || uploadFiles[0].name.replace(/\.[^.]+$/, ''));
        formData.append('description', uploadDesc.trim());
        formData.append('category', '');
        formData.append('folder_id', folderId);
        await libraryApi.upload(formData);
      } else {
        const formData = new FormData();
        uploadFiles.forEach(f => formData.append('files', f));
        formData.append('folder_id', folderId);
        await libraryApi.bulkUpload(formData);
      }
      setShowUpload(false);
      resetUpload();
      loadData();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setUploadFiles([]);
    setUploadTitle('');
    setUploadDesc('');
    setUploadError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  // ─── Folder CRUD ───
  const handleCreateFolder = async () => {
    if (!folderName.trim()) return;
    try {
      await libraryApi.createFolder({ name: folderName.trim(), description: folderDesc.trim() });
      setShowFolderCreate(false);
      setFolderName('');
      setFolderDesc('');
      loadData();
    } catch (err) {
      console.error('Create folder failed:', err);
    }
  };

  const handleRenameFolder = async () => {
    if (!editFolderId || !editFolderName.trim()) return;
    try {
      await libraryApi.updateFolder(editFolderId, { name: editFolderName.trim(), description: editFolderDesc.trim() });
      setEditFolderId(null);
      loadData();
    } catch (err) {
      console.error('Rename folder failed:', err);
    }
  };

  const handleDeleteFolder = async (id) => {
    try {
      await libraryApi.deleteFolder(id);
      setDeleteFolderId(null);
      loadData();
    } catch (err) {
      console.error('Delete folder failed:', err);
    }
  };

  // ─── Asset CRUD ───
  const handleDelete = async (id) => {
    try {
      await libraryApi.delete(id);
      setAssets(prev => prev.filter(a => a.id !== id));
      setDeleteId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const startEditAsset = (asset) => {
    setEditId(asset.id);
    setEditTitle(asset.title);
    setEditDesc(asset.description || '');
  };

  const handleSaveEdit = async () => {
    if (!editId || !editTitle.trim()) return;
    setSaving(true);
    try {
      const updated = await libraryApi.update(editId, { title: editTitle.trim(), description: editDesc.trim() });
      setAssets(prev => prev.map(a => a.id === editId ? { ...a, ...updated } : a));
      setEditId(null);
    } catch (err) {
      console.error('Update failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Helpers ───
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = (type) => ['png', 'jpg', 'jpeg'].includes(type);

  const filtered = assets.filter(a =>
    a.title.toLowerCase().includes(filter.toLowerCase()) ||
    (a.category || '').toLowerCase().includes(filter.toLowerCase())
  );
  const filteredFolders = folders.filter(f =>
    f.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="marco-library" className="min-h-screen bg-[#0D0D0D]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/"><img src={ASSETS.LOGO} alt="YUL On Tap" className="h-10 w-auto" /></Link>
            <div className="h-6 w-px bg-white/10" />
            <span className="font-heading text-lg font-semibold text-white tracking-wide">Marcom</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === 'super_admin' && (
              <Link to="/marcom/admin">
                <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/5">
                  <Shield className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Admin
                </Button>
              </Link>
            )}
            <span className="text-white/50 text-sm hidden sm:block">{user?.name}</span>
            <button onClick={async () => { await logout(); navigate('/portal'); }}
              data-testid="logout-btn"
              className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Sub-nav */}
      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center gap-1">
          <Link to="/marco" className="px-4 py-3 text-sm text-white/40 hover:text-white/60 transition-colors">
            {t('My Marcoms', 'Mes Marcoms')}
          </Link>
          <div className="px-4 py-3 text-sm text-white font-medium border-b-2 border-wine">
            {t('Library', 'Bibliotheque')}
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Breadcrumb + Title */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            {currentFolder ? (
              <div className="flex items-center gap-2 mb-1">
                <button onClick={() => setCurrentFolder(null)} data-testid="back-to-folders"
                  className="flex items-center gap-1.5 text-wine text-sm hover:text-wine-light transition-colors">
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                  {t('Library', 'Bibliotheque')}
                </button>
                <ChevronRight className="w-3.5 h-3.5 text-white/20" strokeWidth={1.5} />
                <span className="text-white/60 text-sm">{currentFolder.name}</span>
              </div>
            ) : null}
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">
              {currentFolder ? currentFolder.name : t('Asset Library', 'Bibliotheque d\'Assets')}
            </h1>
            <p className="text-white/50 mt-1">
              {canManage
                ? (currentFolder
                    ? `${assets.length} ${t('files', 'fichiers')}`
                    : t('Organize and manage presentation assets.', 'Organisez et gerez les assets de presentation.'))
                : t('Browse approved assets for your presentations.', 'Parcourez les assets approuves pour vos presentations.')
              }
            </p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              {!currentFolder && (
                <Button onClick={() => setShowFolderCreate(true)} data-testid="create-folder-btn"
                  variant="outline" className="text-white border-white/20 hover:bg-white/5 rounded-xl h-11">
                  <FolderPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t('New Folder', 'Nouveau Dossier')}
                </Button>
              )}
              <Button onClick={() => setShowUpload(true)} data-testid="upload-asset-btn"
                className="bg-wine hover:bg-wine-light text-white rounded-xl h-11 px-5">
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {t('Upload', 'Telecharger')}
              </Button>
            </div>
          )}
        </div>

        {/* Search */}
        {(assets.length > 0 || folders.length > 0) && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" strokeWidth={1.5} />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)}
              data-testid="library-search"
              placeholder={t('Search...', 'Rechercher...')}
              className="pl-10 bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/25 h-11 rounded-xl" />
          </div>
        )}

        {/* ═══ Upload Modal ═══ */}
        {showUpload && canManage && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div data-testid="upload-modal" className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl font-semibold text-white">
                  {currentFolder
                    ? `${t('Upload to', 'Telecharger dans')} "${currentFolder.name}"`
                    : t('Upload Assets', 'Telecharger des Assets')}
                </h2>
                <button onClick={() => { setShowUpload(false); resetUpload(); }}
                  className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>

              <div onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors mb-4 ${
                  uploadFiles.length > 0 ? 'border-wine/40 bg-wine/5' : 'border-white/10 hover:border-white/20'
                }`}>
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden" multiple
                  onChange={handleFileSelect} data-testid="file-input" />
                {uploadFiles.length > 0 ? (
                  <div className="space-y-1">
                    {uploadFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-center gap-2">
                        <Files className="w-4 h-4 text-wine" strokeWidth={1.5} />
                        <span className="text-white text-sm">{f.name}</span>
                        <span className="text-white/30 text-xs">({formatSize(f.size)})</span>
                      </div>
                    ))}
                    <p className="text-wine/60 text-xs mt-2">{uploadFiles.length} {t('file(s) selected', 'fichier(s) selectionne(s)')}</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-white/20 mx-auto mb-2" strokeWidth={1.5} />
                    <p className="text-white/40 text-sm">{t('Click to select files', 'Cliquez pour selectionner')}</p>
                    <p className="text-white/20 text-xs mt-1">PNG, JPG, PDF — {t('Max 10MB each', 'Max 10 Mo chacun')}</p>
                    <p className="text-wine/50 text-xs mt-1">{t('Select multiple files for bulk upload', 'Selectionnez plusieurs fichiers')}</p>
                  </>
                )}
              </div>

              {uploadFiles.length === 1 && (
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Title', 'Titre')}</label>
                    <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)}
                      data-testid="asset-title-input"
                      className="bg-white/5 border-white/10 text-white h-10 rounded-lg" />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs font-medium block mb-1">{t('Description', 'Description')} <span className="text-white/20">({t('optional', 'optionnel')})</span></label>
                    <Input value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
                      data-testid="asset-desc-input"
                      className="bg-white/5 border-white/10 text-white h-10 rounded-lg" />
                  </div>
                </div>
              )}
              {uploadFiles.length > 1 && (
                <p className="text-white/30 text-xs mb-4">{t('Bulk upload: titles will be set from filenames.', 'Telechargement groupe: les titres seront derives des noms de fichiers.')}</p>
              )}

              {uploadError && <p data-testid="upload-error" className="text-red-400 text-sm mb-3">{uploadError}</p>}

              <div className="flex gap-3">
                <Button onClick={() => { setShowUpload(false); resetUpload(); }} variant="ghost"
                  className="flex-1 text-white/50 hover:text-white hover:bg-white/5 rounded-xl h-11">
                  {t('Cancel', 'Annuler')}
                </Button>
                <Button onClick={handleUpload} disabled={uploadFiles.length === 0 || uploading}
                  data-testid="confirm-upload-btn"
                  className="flex-1 bg-wine hover:bg-wine-light text-white rounded-xl h-11">
                  {uploading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('Uploading...', 'Telechargement...')}
                    </span>
                  ) : (
                    `${t('Upload', 'Telecharger')} ${uploadFiles.length > 1 ? `(${uploadFiles.length})` : ''}`
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Create Folder Modal ═══ */}
        {showFolderCreate && canManage && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div data-testid="create-folder-modal" className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl font-semibold text-white">{t('New Folder', 'Nouveau Dossier')}</h2>
                <button onClick={() => { setShowFolderCreate(false); setFolderName(''); setFolderDesc(''); }}
                  className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-white/60 text-xs font-medium block mb-1">{t('Folder Name', 'Nom du Dossier')} *</label>
                  <Input value={folderName} onChange={(e) => setFolderName(e.target.value)}
                    data-testid="folder-name-input"
                    placeholder={t('e.g., B-Heard Marketing Pics', 'ex., Photos Marketing B-Heard')}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-10 rounded-lg" />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-medium block mb-1">{t('Description', 'Description')} <span className="text-white/20">({t('optional', 'optionnel')})</span></label>
                  <Input value={folderDesc} onChange={(e) => setFolderDesc(e.target.value)}
                    data-testid="folder-desc-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-10 rounded-lg" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <Button onClick={() => { setShowFolderCreate(false); setFolderName(''); setFolderDesc(''); }} variant="ghost"
                  className="flex-1 text-white/50 hover:text-white hover:bg-white/5 rounded-xl h-11">{t('Cancel', 'Annuler')}</Button>
                <Button onClick={handleCreateFolder} disabled={!folderName.trim()} data-testid="confirm-create-folder"
                  className="flex-1 bg-wine hover:bg-wine-light text-white rounded-xl h-11">{t('Create', 'Creer')}</Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Edit Folder Modal ═══ */}
        {editFolderId && canManage && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div data-testid="edit-folder-modal" className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl font-semibold text-white">{t('Edit Folder', 'Modifier le Dossier')}</h2>
                <button onClick={() => setEditFolderId(null)} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="space-y-3">
                <Input value={editFolderName} onChange={(e) => setEditFolderName(e.target.value)}
                  data-testid="edit-folder-name" className="bg-white/5 border-white/10 text-white h-10 rounded-lg" />
                <Input value={editFolderDesc} onChange={(e) => setEditFolderDesc(e.target.value)}
                  placeholder={t('Description', 'Description')}
                  data-testid="edit-folder-desc" className="bg-white/5 border-white/10 text-white h-10 rounded-lg" />
              </div>
              <div className="flex gap-3 mt-5">
                <Button onClick={() => setEditFolderId(null)} variant="ghost"
                  className="flex-1 text-white/50 hover:text-white hover:bg-white/5 rounded-xl h-11">{t('Cancel', 'Annuler')}</Button>
                <Button onClick={handleRenameFolder} disabled={!editFolderName.trim()} data-testid="save-folder-edit"
                  className="flex-1 bg-wine hover:bg-wine-light text-white rounded-xl h-11">{t('Save', 'Sauvegarder')}</Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Edit Asset Modal ═══ */}
        {editId && canManage && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div data-testid="edit-asset-modal" className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl font-semibold text-white">{t('Edit Asset', 'Modifier l\'Asset')}</h2>
                <button onClick={() => setEditId(null)} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="space-y-3">
                <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid="edit-title-input"
                  className="bg-white/5 border-white/10 text-white h-10 rounded-lg" />
                <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} data-testid="edit-desc-input"
                  placeholder={t('Description', 'Description')}
                  className="bg-white/5 border-white/10 text-white h-10 rounded-lg" />
              </div>
              <div className="flex gap-3 mt-5">
                <Button onClick={() => setEditId(null)} variant="ghost"
                  className="flex-1 text-white/50 hover:text-white hover:bg-white/5 rounded-xl h-11">{t('Cancel', 'Annuler')}</Button>
                <Button onClick={handleSaveEdit} disabled={!editTitle.trim() || saving} data-testid="save-edit-btn"
                  className="flex-1 bg-wine hover:bg-wine-light text-white rounded-xl h-11">
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('Save', 'Sauvegarder')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Content ═══ */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-wine border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Folders Grid (root view only) */}
            {currentFolder === null && filteredFolders.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/25 mb-3">{t('Folders', 'Dossiers')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredFolders.map((folder) => (
                    <div key={folder.id} data-testid={`folder-card-${folder.id}`}
                      className="group bg-[#1A1A1A] border border-white/10 rounded-xl p-4 hover:border-wine/30 transition-all cursor-pointer relative">
                      <div className="flex items-center gap-3" onClick={() => setCurrentFolder(folder)}>
                        <div className="w-10 h-10 bg-wine/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FolderOpen className="w-5 h-5 text-wine" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium text-sm truncate">{folder.name}</h4>
                          <span className="text-white/30 text-xs">{folder.asset_count || 0} {t('files', 'fichiers')}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-white/15 group-hover:text-white/30" strokeWidth={1.5} />
                      </div>
                      {/* Admin actions */}
                      {canManage && (
                        <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); setEditFolderId(folder.id); setEditFolderName(folder.name); setEditFolderDesc(folder.description || ''); }}
                            data-testid={`edit-folder-${folder.id}`}
                            className="p-1 text-white/30 hover:text-white rounded hover:bg-white/5"><Pencil className="w-3 h-3" strokeWidth={1.5} /></button>
                          {deleteFolderId === folder.id ? (
                            <div className="flex items-center gap-1 bg-[#111] rounded-lg p-1" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => handleDeleteFolder(folder.id)} data-testid={`confirm-delete-folder-${folder.id}`}
                                className="px-2 py-0.5 bg-red-600 text-white text-[10px] rounded">{t('Delete', 'Suppr.')}</button>
                              <button onClick={() => setDeleteFolderId(null)} className="px-2 py-0.5 text-white/50 text-[10px]">{t('Cancel', 'Non')}</button>
                            </div>
                          ) : (
                            <button onClick={(e) => { e.stopPropagation(); setDeleteFolderId(folder.id); }}
                              data-testid={`delete-folder-${folder.id}`}
                              className="p-1 text-white/30 hover:text-red-400 rounded hover:bg-white/5"><Trash2 className="w-3 h-3" strokeWidth={1.5} /></button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Assets Header */}
            {(currentFolder !== null || filtered.length > 0) && (
              <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/25 mb-3">
                {currentFolder ? t('Files', 'Fichiers') : t('Unfiled Assets', 'Assets Non-Classes')}
              </h3>
            )}

            {/* Assets Grid */}
            {filtered.length === 0 && (currentFolder !== null || folders.length === 0) ? (
              <div data-testid="library-empty" className="text-center py-16">
                <FolderOpen className="w-12 h-12 text-white/15 mx-auto mb-4" strokeWidth={1.5} />
                <h2 className="font-heading text-lg text-white/50 mb-2">
                  {currentFolder ? t('This folder is empty', 'Ce dossier est vide') : t('No assets yet', 'Aucun asset')}
                </h2>
                {canManage && (
                  <Button onClick={() => setShowUpload(true)} variant="outline" className="mt-4 text-white border-white/20 hover:bg-white/5 rounded-xl">
                    <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t('Upload Files', 'Telecharger des Fichiers')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((asset) => (
                  <div key={asset.id} data-testid={`library-asset-${asset.id}`}
                    className="group bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden hover:border-wine/30 transition-all">
                    <div className="aspect-[16/10] bg-[#111] relative overflow-hidden">
                      {isImage(asset.file_type) ? (
                        <img src={libraryApi.fileUrl(asset.id)} alt={asset.title}
                          className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="w-12 h-12 text-white/15" strokeWidth={1.5} />
                          <span className="absolute bottom-2 right-2 text-[10px] font-bold uppercase text-white/20 bg-black/40 px-2 py-0.5 rounded">PDF</span>
                        </div>
                      )}
                      {canManage && (
                        <>
                          {deleteId === asset.id ? (
                            <div className="absolute inset-0 bg-black/80 flex items-center justify-center gap-3">
                              <Button size="sm" onClick={() => handleDelete(asset.id)} data-testid={`confirm-delete-asset-${asset.id}`}
                                className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 rounded-lg">{t('Delete', 'Supprimer')}</Button>
                              <Button size="sm" variant="ghost" onClick={() => setDeleteId(null)}
                                className="text-white/60 hover:text-white text-xs h-8">{t('Cancel', 'Annuler')}</Button>
                            </div>
                          ) : (
                            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => startEditAsset(asset)} data-testid={`edit-asset-${asset.id}`}
                                className="p-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white/40 hover:text-white"><Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                              <button onClick={() => setDeleteId(asset.id)} data-testid={`delete-asset-${asset.id}`}
                                className="p-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white/40 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading text-sm font-medium text-white truncate">{asset.title}</h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-white/30">
                        <span className="uppercase font-semibold">{asset.file_type}</span>
                        <span>{formatSize(asset.file_size)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default MarcoLibrary;
